import { detectPii } from '../helpers/detectPii'
import { queryParamHas } from '../helpers/getQueryParam'
import type WebModel from '../models/WebModel'
import PageSettings, {
  checkExpiration,
  parseKey,
  isBlocklistAttribute,
  isExpirationAttribute
} from '../pageSettings/PageSettings'
import {
  ALL,
  BLOCKLIST,
  BLOCKLIST_ATTR,
  DEPRECATED_BLOCKLIST,
  DEPRECATED_BLOCKLIST_ATTR
} from '../constants'
import type { Slot } from '../slots/Slot'
import { isTruthyAttribute } from '../helpers/DOM/isTruthyAttribute'
import Denque from 'denque'

export interface IBlocklistRegistry {
  [path: string]: ISettings
}

export default class Blocklist {
  private registry: IBlocklistRegistry = {}
  private absoluteRegistry: IBlocklistRegistry = {}
  private relativeRegistry: IBlocklistRegistry = {}

  constructor(
    private webModel: WebModel,
    private pageSettings: PageSettings,
    private _window: Window = window
  ) {
    this.initRegistry()
    this.absoluteRegistry = this.computeAbsoluteRegistry()
    this.relativeRegistry = this.computeRelativeRegistry()
  }

  // Returns true if any `all` blocklist setting is true.
  get all(): boolean {
    const currentPath = this.getCurrentPath()
    const currentPathBlocklist = this.absoluteRegistry[currentPath] || {}
    const pageSettingsBlocklist = this.pageSettings.blocklist || {}

    const all =
      detectPii(this._window.location.href) ||
      queryParamHas('test', 'killswitch', this._window.location.search) ||
      currentPathBlocklist.all ||
      pageSettingsBlocklist.all === '1' ||
      this.isAdunitBlocklistedByRelativeRegistry('all', currentPath)

    return !!all
  }

  /**
   * Take in UrlFilters and PageSettings
   * if current path matches exactly to any UrlFilter.path && !UrlFilter.starts_with && UrlFilter.settings['adunit']
   * if current path matches partially to any UrlFilter.path && UrlFilter.starts_with
   * Check if string is truthy in that settings object or if this.all is truthy
   * Check if string is truthy on pageSettings.blocklist
   * return boolean
   */
  contains(adunitName: keyof ISettings | string): boolean {
    const currentPath = this.getCurrentPath()
    const currentPathBlocklist = this.absoluteRegistry[currentPath] || {}
    const pageSettingsBlocklist = this.pageSettings.blocklist || {}

    const adunitSetting = mapAdunitToISettings(
      adunitName
    ).toLowerCase() as keyof ISettings

    const relativePathBlocklisted = this.isAdunitBlocklistedByRelativeRegistry(
      adunitSetting,
      currentPath
    )

    const blocklistedByAllSetting = this.all

    return (
      blocklistedByAllSetting ||
      isTruthyAttribute(currentPathBlocklist[adunitSetting]) ||
      isTruthyAttribute(pageSettingsBlocklist[adunitSetting]) ||
      relativePathBlocklisted
    )
  }

  isBlockListed(slot: Slot): boolean {
    const { adUnitId, target } = slot
    return (
      this.checkOnPageBlockList(adUnitId, target as HTMLElement) ||
      this.contains(adUnitId)
    )
  }

  // returns a registry of urlFilters where 'starts_with' !== true
  private computeAbsoluteRegistry(): IBlocklistRegistry {
    const absoluteRegistry = {}
    for (const key in this.registry) {
      const urlFilter = this.registry[key]
      if (!urlFilter.starts_with) {
        absoluteRegistry[key] = lowercaseKeys(urlFilter)
      }
    }

    return absoluteRegistry
  }

  // returns a registry of urlFilters where 'starts_with' === true
  private computeRelativeRegistry(): IBlocklistRegistry {
    const relativeRegistry = {}
    for (const key in this.registry) {
      const urlFilter = this.registry[key]
      if (urlFilter.starts_with) {
        relativeRegistry[key] = lowercaseKeys(urlFilter)
      }
    }

    return relativeRegistry
  }

  /**
   * Checks the relativeRegistry for paths that match the currentPath
   * (i.e. if the currentPath starts with relativePath), and returns
   * true if any matching rules blocklist the passed in adunit
   *
   * @param adunit
   * @param currentPath
   */
  private isAdunitBlocklistedByRelativeRegistry(
    adunit: keyof ISettings,
    currentPath: string
  ): boolean {
    // The keys are paths. e.g. '/foo/bar'
    const allRelativePathKeys = Object.keys(this.relativeRegistry)
    const matchingPathKeys = allRelativePathKeys.filter((relativePath) =>
      this.isChildPath(relativePath, currentPath)
    )

    // Check the urlFilters in relativeRegistry with each key in matchingPathKeys.
    // Returns true if any filter blocklists the adunit
    const relativePathBlocklisted = matchingPathKeys.reduce(
      (isBlocklisted, matchingPathKey) => {
        return this.relativeRegistry[matchingPathKey][adunit] || isBlocklisted
      },
      false
    )

    return relativePathBlocklisted
  }

  private initRegistry(): void {
    this.webModel.urlFilters.forEach((urlFilter) =>
      this.registerSettingsToPath(urlFilter)
    )
  }

  private registerSettingsToPath({ path, settings }: IUrlFilter): void {
    const formattedPath = this.formatPath(path)
    if (!this.registry[formattedPath]) {
      this.registry[formattedPath] = {}
    }

    this.registry[formattedPath] = {
      ...this.registry[formattedPath],
      ...settings
    }
  }

  private getCurrentPath(): string {
    const currentpath = this._window.location.pathname
      .replace(/\.[^/.]+$/, '') // remove file extension
      .replace(/\/$/, '') // remove final slash

    return currentpath || '/'
  }

  // For test cases,
  // `https://domain.com/a/b/c.html` should return `/a/b/c`,
  // `https://domain.com/` should return `/`,
  // `/a/b/c.html` should return `/a/b/c`
  private formatPath(path: string): string {
    const url = document.createElement('a')
    url.href = path

    const newPath = url.pathname
      .replace(/\.[^/.]+$/, '') // remove file extension
      .replace(/\/$/, '') // remove final slash

    return newPath.length ? newPath : '/'
  }

  /**
   * Checks to see if the childPath is indeed a child path of parentPath
   * e.g. '/a/b/c' is a child path of '/a/b
   * e.g. '/a/bacon/c' is not a child path of '/a/b'
   * @param parentPath
   * @param childPath
   */
  private isChildPath(parentPath: string, childPath: string): boolean {
    if (parentPath === '/') {
      // '/' is the parent of every child path
      return true
    }

    const childPieces = childPath.split('/')
    const parentPieces = parentPath.split('/')

    // check to make sure that every piece of parentPieces is equal to
    // it's matching index in childPieces.
    return parentPieces.every((piece, index) => {
      return childPieces[index] === piece
    })
  }

  /**
   * Checks to see if the HTMLElement's parent contains a
   * data-attribute for on-page blocklisting
   * @param element
   */
  private checkOnPageBlockList(adUnitId: string, target: HTMLElement): boolean {
    // check element's dataset for anything beginning with 'blocklist'
    // then inspect which sort of blocklisting should happen after traversal
    function hasBlockListSetting(element: HTMLElement): boolean {
      // inspect the element's dataset, as there's presumably
      // less to iterate over than attributes
      const dataset = Object.keys(element.dataset)
      return (
        dataset.length > 0 &&
        dataset.some(
          (d) =>
            d.indexOf(DEPRECATED_BLOCKLIST) !== -1 ||
            d.indexOf(BLOCKLIST) !== -1
        )
      )
    }

    function inspectBlocklistSettings(
      attributes: NamedNodeMap,
      adUnitId: string
    ): boolean {
      const dataAttrs = Array.from(attributes)
      const dataBlocklist = dataAttrs.filter(isBlocklistAttribute)
      const dataExpiresAt = dataAttrs.filter(isExpirationAttribute).pop()
      const isExpired = dataExpiresAt && checkExpiration(dataExpiresAt)
      const settings = dataBlocklist.map(
        (a) =>
          a.value === '1' &&
          (parseKey(a, DEPRECATED_BLOCKLIST_ATTR) ||
            parseKey(a, BLOCKLIST_ATTR))
      )
      return !isExpired && settings.some((s) => s === adUnitId || s === ALL)
    }

    function ascend(target: HTMLElement): boolean {
      let isBlocklisted = false
      let blocklistSettings = false
      let attributes = target.attributes
      let parent = target.parentElement

      while (parent && !blocklistSettings) {
        blocklistSettings = hasBlockListSetting(parent)
        attributes = parent.attributes
        parent = parent.parentElement
      }

      if (blocklistSettings) {
        isBlocklisted = inspectBlocklistSettings(attributes, adUnitId)
      }

      return isBlocklisted
    }

    function descend(target: HTMLElement): boolean {
      let isBlocklisted = false
      let blocklistSettings = false
      let attributes = target.attributes
      const queue = new Denque<HTMLElement>()

      // start iterations with target
      let child: HTMLElement | undefined = target
      while (child) {
        if (hasBlockListSetting(child)) {
          attributes = child.attributes
          blocklistSettings = true
          break
        }

        for (let n = 0; n < child.children.length; n++) {
          queue.push(child.children[n] as HTMLElement)
        }

        child = queue.shift()
      }

      if (blocklistSettings) {
        isBlocklisted = inspectBlocklistSettings(attributes, adUnitId)
      }

      return isBlocklisted
    }

    if (target) {
      // handles edge case for adhesions targeting <body> element
      return target === document.body ? descend(target) : ascend(target)
    }

    return false
  }
}

export function mapAdunitToISettings(key: string): string {
  const adunitToISettingsMap: { [key: string]: keyof ISettings } = {
    adhesion_desktop: 'adhesion_desktop',
    adhesion_mobile: 'adhesion_mobile',
    adhesion_tablet: 'adhesion_tablet',
    all: 'all',
    chicory: 'chicory',
    content_btf: 'content_desktop',
    content_desktop: 'content_desktop',
    content_mobile: 'content_mobile',
    in_image: 'in_image',
    in_screen: 'in_screen',
    interstitial_mobile: 'interstitial_mobile',
    interstitial_desktop: 'interstitial_desktop',
    leaderboard_atf: 'leaderboard',
    leaderboard_btf: 'leaderboard',
    leaderboard: 'leaderboard',
    readmore: 'readmore',
    recipe: 'recipe',
    recipe_btf: 'recipe',
    recipe_mobile: 'recipe',
    sidebar_atf: 'sidebar_atf',
    sidebar_btf: 'sidebar_btf',
    zergnet: 'zergnet'
  }

  return adunitToISettingsMap[key] || key
}

function lowercaseKeys(obj: object): object {
  const newObj = {}
  Object.keys(obj).map((k) => {
    newObj[k.toLowerCase()] = obj[k]
  })

  return newObj
}
